import {
  CompetitionGenderEnum,
  EventGroupFormatsEnum,
  TeamTypesEnum,
} from 'types/generated/client';

export const INITIAL_PAGE_SIZE = 25;

// Max featured (boosted) tournaments surfaced at the top — small on purpose.
export const MAX_FEATURED_TOURNAMENTS = 3;

export const MINIMUM_COST = 0;
export const MAXIMUM_COST = 200;
export const MINIMUM_AGE = 0;
export const MAXIMUM_AGE = 99;

export function formatDateRange(
  startDate: Date,
  endDate: Date,
  timezone?: string | null,
): string {
  // Format dates in the event's local timezone — viewers in other zones
  // should still see the tournament's posted date, not a shifted version.
  const tz =
    timezone && timezone.trim() ? timezone : Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const monthDayFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    month: 'long',
    day: 'numeric',
  });

  // Same-day check needs to happen in the event's timezone too, otherwise a
  // late-night event can split across two days for a viewer in another zone.
  const dayKeyFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const isSameDay = dayKeyFmt.format(startDate) === dayKeyFmt.format(endDate);

  if (isSameDay) {
    return dateFormatter.format(startDate);
  } else {
    return `${monthDayFormatter.format(startDate)} - ${dateFormatter.format(endDate)}`;
  }
}

export const TOURNAMENT_DISTANCE_IMPERIAL_OPTIONS = [
  {
    name: 'Any distance',
    id: 0,
  },
  {
    name: '10 miles',
    id: 10,
  },
  {
    name: '25 miles',
    id: 25,
  },
  {
    name: '50 miles',
    id: 50,
  },
  {
    name: '100 miles',
    id: 100,
  },
  {
    name: '200 miles',
    id: 200,
  },
  {
    name: '500 miles',
    id: 500,
  },
];

export const ANY_OPTION = 'Any';

export type TournamentTypeFilter = 'ALL' | 'TOURNAMENTS' | 'ROUND_ROBINS';

/**
 * Reuses the existing VAIR event-type art, which is what `HostedEvents/data.tsx`
 * already uses for the Tournament / Round Robin event types.
 *
 * Rendered in the page header from `xl` up and inside the filters modal below it, so
 * the switch is reachable at every width.
 */
export const TOURNAMENT_TYPE_OPTIONS: {
  label: string;
  value: TournamentTypeFilter;
  iconSrc?: string;
  iconClassName?: string;
}[] = [
    { label: 'All types', value: 'ALL' },
    {
      label: 'Tournaments',
      value: 'TOURNAMENTS',
      iconSrc: '/images/vair/vair-tournament.png',
      iconClassName: 'h-4 w-4',
    },
    {
      label: 'Round Robins',
      value: 'ROUND_ROBINS',
      iconSrc: '/images/vair/vair-round-robin.png',
      iconClassName: 'h-4 w-4',
    },
  ];


const ROUND_ROBIN_GROUP_FORMATS: EventGroupFormatsEnum[] = [
  EventGroupFormatsEnum.RoundRobin,
  EventGroupFormatsEnum.VairRoundRobin,
];


export const matchesTournamentType = (
  type: TournamentTypeFilter,
  groupFormat?: EventGroupFormatsEnum | null,
): boolean => {
  if (type === 'ALL') {
    return true;
  }

  const isRoundRobin = !!groupFormat && ROUND_ROBIN_GROUP_FORMATS.includes(groupFormat);

  return type === 'ROUND_ROBINS' ? isRoundRobin : !isRoundRobin;
};

// Singles first, and no explicit "Any" chip — the design shows two options and an
// unset default. Clicking the selected chip clears back to null (= no filter).
export const TEAM_TYPES = [
  {
    id: TeamTypesEnum.Singles,
    name: 'Singles',
  },
  {
    id: TeamTypesEnum.Doubles,
    name: 'Doubles',
  },
];

/**
 * Moneyball is not an `EventGroupFormatsEnum` value — it is its own `isMoneyBall`
 * boolean on the division, and a Moneyball division still carries a real format. So
 * the chip stores this sentinel and `useTournament` filters on the boolean instead of
 * comparing formats.
 */
export const MONEYBALL_FORMAT_ID = 'MONEYBALL';

export const COMPETITION_FORMATS = [
  {
    id: EventGroupFormatsEnum.RoundRobin,
    name: 'Round Robin (RR)',
  },
  {
    id: EventGroupFormatsEnum.RoundRobinSingleElimination,
    name: 'RR + Single Elimination',
  },
  {
    id: MONEYBALL_FORMAT_ID,
    name: 'Moneyball',
  },
];

export const COMPETITION_GENDER_MENU = [
  { id: CompetitionGenderEnum.Male, name: "Men's" },
  {
    id: CompetitionGenderEnum.Female,
    name: "Women's",
  },
  {
    id: CompetitionGenderEnum.Mixed,
    name: 'Mixed',
  },
];

export const TOURNAMENT_DATES = [
  {
    name: 'Next 7 Days',
    id: 7,
  },
  {
    name: 'Next 30 Days',
    id: 30,
  },
  {
    name: 'Next 90 Days',
    id: 90,
  },
  {
    name: 'Within a Year',
    id: 370,
  },
  {
    name: 'Any Date',
    id: -1,
  },
];
